/**
 * InviteMembersDialog — org-level invite (Administration Overview).
 *
 * Platform invite roles match AdminInviteDto: Admin | Member | Viewer.
 * Wire payload uses Admin | Member | Guest (DTO); Viewer maps to Guest.
 * Only an Admin can reach this dialog or send these invites (server-enforced).
 *
 * Compact modal with email input, role selector, optional workspace
 * assignment, two-phase form → results.
 */
import { useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  Check,
  CheckCircle,
  ChevronDown,
  Eye,
  Loader2,
  Shield,
  Users,
  X,
  XCircle,
} from "lucide-react";
import { Modal } from "@/components/ui/overlay/Modal";
import { Button } from "@/components/ui/button/Button";
import {
  administrationApi,
  type WorkspaceSnapshotRow,
} from "@/features/administration/api/administration.api";

/* ── Types + role config ────────────────────────────────────────── */

interface InviteMembersDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  contextLabel?: string;
}

/** Result banner mode — used by UI and gating tests. */
export type InviteResultBannerMode = "success" | "partial" | "failure";

export function inviteResultBannerMode(
  successCount: number,
  total: number,
): InviteResultBannerMode {
  if (total <= 0 || successCount <= 0) return "failure";
  if (successCount < total) return "partial";
  return "success";
}

/** UI labels — Viewer is the product name for DTO Guest. */
type RoleId = "Admin" | "Member" | "Viewer";

const ROLES: ReadonlyArray<{
  id: RoleId;
  name: string;
  description: string;
  icon: typeof Users;
  colors: { bg: string; text: string };
  recommended?: boolean;
}> = [
  {
    id: "Admin",
    name: "Admin",
    description:
      "Can invite people, change roles and manage organization settings",
    icon: Shield,
    colors: { bg: "bg-indigo-100", text: "text-indigo-600" },
  },
  {
    id: "Member",
    name: "Member",
    description:
      "Can create, edit, and collaborate in assigned workspaces",
    icon: Users,
    colors: { bg: "bg-emerald-100", text: "text-emerald-600" },
    recommended: true,
  },
  {
    id: "Viewer",
    name: "Viewer",
    description: "Read-only access to shared projects and tasks",
    icon: Eye,
    colors: { bg: "bg-amber-100", text: "text-amber-600" },
  },
];

/** Map UI role → AdminInviteDto platformRole (Admin | Member | Guest). */
export function toAdminInviteWireRole(
  role: RoleId,
): "Admin" | "Member" | "Guest" {
  if (role === "Admin") return "Admin";
  return role === "Viewer" ? "Guest" : "Member";
}

/** Workspace access level for an invite: an Admin joins workspaces as a Member. */
function toWorkspaceAccessLevel(role: RoleId): "Member" | "Guest" {
  return role === "Viewer" ? "Guest" : "Member";
}

type InviteResult = {
  email: string;
  status: "success" | "error";
  message?: string;
  /**
   * EMAIL-TRUTH-1: SUPPRESSED — the invite was created but the staging
   * recipient allow-list will not deliver its email. Never shown as "Sent".
   */
  delivery?: "QUEUED" | "SUPPRESSED";
  deliveryReason?: string;
};

function isSuppressed(r: InviteResult): boolean {
  return r.status === "success" && r.delivery === "SUPPRESSED";
}

/* ── Component ──────────────────────────────────────────────────── */

export function InviteMembersDialog({
  isOpen,
  onClose,
  onSuccess,
  contextLabel: _contextLabel,
}: InviteMembersDialogProps) {
  const [emails, setEmails] = useState("");
  const [selectedRole, setSelectedRole] = useState<RoleId>("Member");
  const [selectedWorkspaceIds, setSelectedWorkspaceIds] = useState<string[]>(
    [],
  );
  const [workspaces, setWorkspaces] = useState<WorkspaceSnapshotRow[]>([]);
  const [workspacesStatus, setWorkspacesStatus] = useState<
    "idle" | "loading" | "ready" | "error"
  >("idle");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [results, setResults] = useState<InviteResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [roleOpen, setRoleOpen] = useState(false);

  const roleRef = useRef<HTMLDivElement>(null);

  function loadWorkspaces() {
    setWorkspacesStatus("loading");
    setWorkspaces([]);
    administrationApi
      .listWorkspaces()
      .then((ws) => {
        setWorkspaces(ws.filter((w) => w.status === "ACTIVE"));
        setWorkspacesStatus("ready");
      })
      .catch(() => {
        setWorkspaces([]);
        setWorkspacesStatus("error");
      });
  }

  // Reset on open.
  useEffect(() => {
    if (isOpen) {
      setEmails("");
      setSelectedRole("Member");
      setSelectedWorkspaceIds([]);
      setResults(null);
      setError(null);
      setRoleOpen(false);
      loadWorkspaces();
    }
  }, [isOpen]);

  // Outside click closes role dropdown.
  useEffect(() => {
    if (!roleOpen) return;
    const handler = (e: MouseEvent) => {
      if (roleRef.current && !roleRef.current.contains(e.target as Node)) {
        setRoleOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [roleOpen]);

  // ── Submit ──
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  async function handleSubmit() {
    const parsed = emails
      .split(/[,;\n]/)
      .map((e) => e.trim())
      .filter(Boolean);

    const invalid = parsed.filter((e) => !emailRegex.test(e));
    if (invalid.length > 0) {
      setError(
        `Invalid email${invalid.length > 1 ? "s" : ""}: ${invalid.join(", ")}`,
      );
      return;
    }
    if (parsed.length === 0) {
      setError("Enter at least one email address");
      return;
    }

    setError(null);
    setIsSubmitting(true);

    const wireRole = toAdminInviteWireRole(selectedRole);
    const workspaceAssignments =
      selectedWorkspaceIds.length > 0
        ? selectedWorkspaceIds.map((wsId) => ({
            workspaceId: wsId,
            accessLevel: toWorkspaceAccessLevel(selectedRole),
          }))
        : undefined;

    try {
      const res = await administrationApi.inviteUsers({
        emails: parsed,
        platformRole: wireRole,
        workspaceAssignments,
      });
      setResults(res.results);
    } catch {
      setResults(
        parsed.map((email) => ({
          email,
          status: "error" as const,
          message: "Failed to send. Try again.",
        })),
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  function resetForm() {
    setEmails("");
    setSelectedRole("Member");
    setSelectedWorkspaceIds([]);
    setResults(null);
    setError(null);
  }

  function handleDone() {
    const successCount =
      results?.filter((r) => r.status === "success").length ?? 0;
    if (successCount > 0) {
      onSuccess?.();
    }
    onClose();
  }

  function toggleWorkspace(wsId: string) {
    setSelectedWorkspaceIds((prev) =>
      prev.includes(wsId)
        ? prev.filter((id) => id !== wsId)
        : [...prev, wsId],
    );
  }

  const activeRole = ROLES.find((r) => r.id === selectedRole)!;
  const ActiveIcon = activeRole.icon;

  // ══════════════════════════════════════════════════════════════════
  // RESULTS STATE
  // ══════════════════════════════════════════════════════════════════
  if (results) {
    const successCount = results.filter((r) => r.status === "success").length;
    const bannerMode = inviteResultBannerMode(successCount, results.length);
    // EMAIL-TRUTH-1: a created invite whose email will not be delivered is not
    // "sent" — the banner counts only deliveries.
    const suppressedCount = results.filter(isSuppressed).length;
    const sentCount = successCount - suppressedCount;
    const failedCount = results.length - successCount;
    const bannerChrome =
      bannerMode !== "failure" && suppressedCount > 0
        ? {
            wrap: "rounded-xl bg-amber-50 p-5 text-center",
            Icon: AlertTriangle,
            iconClass: "h-6 w-6 text-amber-600",
            title:
              sentCount === 0 ? "Invitations not delivered" : "Partially sent",
            subtitle: `${sentCount} of ${results.length} invitation${
              results.length !== 1 ? "s" : ""
            } sent. ${suppressedCount} not delivered — staging allow-list.${
              failedCount > 0 ? " Review failures below." : ""
            }`,
          }
        : bannerMode === "success"
        ? {
            wrap: "rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 p-5 text-center",
            Icon: CheckCircle,
            iconClass: "h-6 w-6 text-emerald-500",
            title: "Invitations sent",
            subtitle: `${successCount} of ${results.length} invitation${
              results.length !== 1 ? "s" : ""
            } sent successfully`,
          }
        : bannerMode === "partial"
          ? {
              wrap: "rounded-xl bg-amber-50 p-5 text-center",
              Icon: AlertTriangle,
              iconClass: "h-6 w-6 text-amber-600",
              title: "Partially sent",
              subtitle: `${successCount} of ${results.length} invitation${
                results.length !== 1 ? "s" : ""
              } sent successfully. Review failures below.`,
            }
          : {
              wrap: "rounded-xl bg-red-50 p-5 text-center",
              Icon: XCircle,
              iconClass: "h-6 w-6 text-red-600",
              title: "Invitations failed",
              subtitle:
                results.length === 1
                  ? "The invitation could not be sent. Try again."
                  : `0 of ${results.length} invitations sent. Try again.`,
            };
    const BannerIcon = bannerChrome.Icon;

    return (
      <Modal
        isOpen={isOpen}
        onClose={handleDone}
        size="sm"
        showCloseButton={false}
        className="!border-0 rounded-2xl bg-white shadow-2xl"
      >
        <div className="space-y-5">
          <div
            className={bannerChrome.wrap}
            data-testid="invite-result-banner"
            data-banner-mode={bannerMode}
          >
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-sm">
              <BannerIcon className={bannerChrome.iconClass} aria-hidden />
            </div>
            <h3 className="mb-1 text-base font-bold text-gray-900">
              {bannerChrome.title}
            </h3>
            <p className="text-sm font-medium text-gray-600">
              {bannerChrome.subtitle}
            </p>
          </div>

          {/* Per-email results */}
          <div className="space-y-1.5">
            {results.map((r, i) => (
              <div
                key={i}
                className="flex items-center justify-between rounded-xl bg-gray-50 px-3 py-2.5"
              >
                <span className="text-sm font-medium text-gray-900">
                  {r.email}
                </span>
                {isSuppressed(r) ? (
                  <span
                    className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800"
                    title={r.message}
                  >
                    Not delivered — staging allow-list
                  </span>
                ) : r.status === "success" ? (
                  <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700">
                    Sent
                  </span>
                ) : (
                  <span
                    className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-red-700"
                    title={r.message}
                  >
                    Failed
                  </span>
                )}
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-1">
            <Button variant="outline" className="flex-1" onClick={resetForm}>
              Invite more
            </Button>
            <Button
              className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-md hover:shadow-lg"
              onClick={handleDone}
            >
              Done
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  // ══════════════════════════════════════════════════════════════════
  // FORM STATE
  // ══════════════════════════════════════════════════════════════════
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      showCloseButton={false}
      className="!border-0 rounded-2xl bg-white shadow-2xl"
    >
      {/* ── Header ── */}
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900">
          Invite people
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          aria-label="Close" title="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="space-y-6">
        {/* ── Email input ── */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-500">
            Invite by email
          </label>
          <input
            type="text"
            value={emails}
            onChange={(e) => {
              setEmails(e.target.value);
              setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSubmit();
              }
            }}
            placeholder="Email, comma or space separated"
            className={`h-12 w-full rounded-xl border bg-gray-50 px-4 text-sm text-gray-900 placeholder:text-gray-400 outline-none transition-all focus:ring-2 focus:ring-blue-500/20 ${
              error ? "border-red-300 focus:border-red-400" : "border-gray-200 focus:border-blue-500"
            }`}
          />
          {error && (
            <p className="text-xs font-medium text-red-500">{error}</p>
          )}
        </div>

        {/* ── Role selector ── */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-500">
            Invite as
          </label>
          <p className="text-xs text-gray-500">
            {selectedRole === "Admin"
              ? "Admins have full control of this organization. Invite one only if you trust them with it."
              : "Choose what this person can do once they accept."}
          </p>
          <div className="relative" ref={roleRef}>
            <button
              type="button"
              onClick={() => setRoleOpen((v) => !v)}
              className={`w-full rounded-xl border bg-gray-50 p-3 text-left transition-all ${
                roleOpen
                  ? "border-blue-500 ring-2 ring-blue-500/20"
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${activeRole.colors.bg}`}
                >
                  <ActiveIcon
                    className={`h-5 w-5 ${activeRole.colors.text}`}
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-gray-900">
                      {activeRole.name}
                    </span>
                    {activeRole.recommended && (
                      <span className="rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
                        Recommended
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 text-sm font-medium text-gray-600">
                    {activeRole.description}
                  </p>
                </div>
                <ChevronDown
                  className={`h-5 w-5 shrink-0 text-gray-400 transition-transform ${
                    roleOpen ? "rotate-180" : ""
                  }`}
                />
              </div>
            </button>

            {roleOpen && (
              <div className="absolute left-0 right-0 top-full z-30 mt-1 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl">
                {ROLES.map((role) => {
                  const RIcon = role.icon;
                  const isSelected = selectedRole === role.id;
                  return (
                    <button
                      key={role.id}
                      type="button"
                      onClick={() => {
                        setSelectedRole(role.id);
                        setRoleOpen(false);
                      }}
                      className={`w-full p-3 text-left transition-colors ${
                        isSelected
                          ? "bg-gradient-to-r from-blue-50 to-cyan-50"
                          : "hover:bg-blue-50/50"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${role.colors.bg}`}
                        >
                          <RIcon
                            className={`h-5 w-5 ${role.colors.text}`}
                          />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-bold text-gray-900">
                              {role.name}
                            </span>
                            {role.recommended && (
                              <span className="rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
                                Recommended
                              </span>
                            )}
                          </div>
                          <p className="mt-0.5 text-sm font-medium text-gray-600">
                            {role.description}
                          </p>
                        </div>
                        {isSelected && (
                          <Check className="h-5 w-5 shrink-0 text-blue-500" />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* ── Workspace assignment ── */}
        {workspacesStatus === "loading" && (
          <div
            className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-600"
            data-testid="invite-workspaces-loading"
          >
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            Loading workspaces…
          </div>
        )}
        {workspacesStatus === "error" && (
          <div
            className="space-y-2 rounded-xl border border-red-200 bg-red-50 px-3 py-3"
            data-testid="invite-workspaces-error"
            role="alert"
          >
            <p className="text-sm font-medium text-red-800">
              Couldn&apos;t load workspaces
            </p>
            <p className="text-xs text-red-700">
              Workspace assignment is unavailable until this loads. You can still
              send invites without assigning a workspace.
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={loadWorkspaces}
              data-testid="invite-workspaces-retry"
            >
              Retry
            </Button>
          </div>
        )}
        {workspacesStatus === "ready" && workspaces.length > 0 && (
          <div className="space-y-2" data-testid="invite-workspaces-list">
            <label className="text-sm font-semibold text-gray-900">
              Add to workspaces{" "}
              <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <div className="max-h-[120px] divide-y divide-gray-100 overflow-y-auto rounded-xl border border-gray-300">
              {workspaces.map((ws) => (
                <label
                  key={ws.workspaceId}
                  className="flex cursor-pointer items-center gap-3 px-3 py-2.5 transition-colors hover:bg-blue-50/50"
                >
                  <input
                    type="checkbox"
                    checked={selectedWorkspaceIds.includes(ws.workspaceId)}
                    onChange={() => toggleWorkspace(ws.workspaceId)}
                    className="h-4 w-4 rounded border-gray-300 text-blue-500 focus:ring-blue-500/20"
                  />
                  <span className="text-sm font-medium text-gray-900">
                    {ws.workspaceName}
                  </span>
                </label>
              ))}
            </div>
            <p className="text-xs text-gray-600">
              Members can see all projects within assigned workspaces.
            </p>
          </div>
        )}

        {/* ── Footer ── */}
        <div className="flex items-center justify-end gap-3 pt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || emails.trim().length === 0}
            className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-all hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              "Send invite"
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
}
